import React, { useEffect, useState } from "react";
import ConfirmAddSocieteWithAdmin from "../../../ui/ConfirmAddSocieteWithAdmin";
import {
  applyDefaultsChoice,
  validateSocieteFormByField,
} from "../../../utils/addAdminSocieteValidation";

const CODES_CATEGORIE = ["P", "M", "C"];
const TYPES_SOCIETE = ["Personne physique", "Personne morale"];
const REGIMES_TVA = ["Normale", "Exportatrice"];
const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2 Mo

const EMPTY_PERSON = {
  nom: "",
  prenom: "",
  cin: "",
  email: "",
  phone: "",
  password: "",
  rePassword: "",
};

const EMPTY_SOCIETE = {
  title: "",
  matricule: "",
  numberEtabSecondaire: "",
  timbre: "",
  email: "",
  phone: "",
  adresse: "",
  codeCategorie: "P",
  typeSociete: "Personne physique",
  codeTVA: "",
  typeTVA: "",
  tva: "0",
  isServiceOnly: 0,
};

const NO_DEFAULTS = { centralWarehouse: false, catalog: false, managerAccess: false };

// The page is green (the colour of the brand): Bootstrap's checked boxes, switches and focus rings are blue.
const GREEN = "#28a745";
const GreenStyle = () => (
  <style>{`
    .noryx-green .custom-control-input:checked ~ .custom-control-label::before {
      background-color: ${GREEN};
      border-color: ${GREEN};
    }
    .noryx-green .custom-control-input:focus ~ .custom-control-label::before {
      box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
    }
    .noryx-green .custom-control-input:focus:not(:checked) ~ .custom-control-label::before {
      border-color: ${GREEN};
    }
    .noryx-green .form-control:focus:not(.is-invalid) {
      border-color: ${GREEN};
      box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
    }
  `}</style>
);

// ---- small building blocks ---------------------------------------------------------------------------

const Card = ({ icon, title, subtitle, tone = "success", children }) => (
  <div className={`card card-outline card-${tone} shadow-sm`}>
    <div className="card-header">
      <h3 className="card-title">
        <i className={`fas ${icon} mr-2 text-${tone}`} />
        {title}
      </h3>
      {subtitle && <div className="text-muted small" style={{ clear: "both" }}>{subtitle}</div>}
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const Group = ({ title, children }) => (
  <>
    <div className="text-uppercase text-muted font-weight-bold small mb-2">{title}</div>
    <div className="row">{children}</div>
  </>
);

// The label is bound to its input (unique id); `hint` is the expected format, `error` is shown under the input.
const FieldShell = ({ id, label, hint, col, error, help, children }) => (
  <div className={`form-group ${col}`}>
    <label htmlFor={id}>
      {label}
      {hint && <small className="text-muted ml-1">({hint})</small>}
    </label>
    {children}
    {error && (
      <div id={`${id}-error`} className="invalid-feedback d-block">
        {error}
      </div>
    )}
    {!error && help && <small className="form-text text-muted">{help}</small>}
  </div>
);

const Field = ({ id, label, hint, col = "col-md-4", error, help, ...inputProps }) => (
  <FieldShell id={id} label={label} hint={hint} col={col} error={error} help={help}>
    <input
      id={id}
      className={`form-control${error ? " is-invalid" : ""}`}
      aria-invalid={error ? "true" : undefined}
      aria-describedby={error ? `${id}-error` : undefined}
      {...inputProps}
    />
  </FieldShell>
);

const SelectField = ({ id, label, col = "col-md-4", error, options, placeholder, ...selectProps }) => (
  <FieldShell id={id} label={label} col={col} error={error}>
    <select
      id={id}
      className={`form-control${error ? " is-invalid" : ""}`}
      aria-invalid={error ? "true" : undefined}
      aria-describedby={error ? `${id}-error` : undefined}
      {...selectProps}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </FieldShell>
);

const PasswordField = ({ id, label, hint, col = "col-md-6", error, ...inputProps }) => {
  const [shown, setShown] = useState(false);
  return (
    <FieldShell id={id} label={label} hint={hint} col={col} error={error}>
      <div className="input-group">
        <input
          id={id}
          type={shown ? "text" : "password"}
          autoComplete="new-password"
          className={`form-control${error ? " is-invalid" : ""}`}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          {...inputProps}
        />
        <div className="input-group-append">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShown(!shown)}
            aria-label={shown ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            title={shown ? "Masquer le mot de passe" : "Afficher le mot de passe"}
          >
            <i className={`fas ${shown ? "fa-eye-slash" : "fa-eye"}`} />
          </button>
        </div>
      </div>
    </FieldShell>
  );
};

// The access of a person (admin or warehouse manager) of the new company.
const PersonFields = ({ prefix, person, onChange, errorOf }) => {
  const field = (name) => ({
    id: `${prefix}-${name}`,
    value: person[name],
    error: errorOf(`${prefix}-${name}`),
    onChange: (e) => onChange(name, e.target.value),
  });
  return (
    <div className="row">
      <Field label="Nom" placeholder="Nom" type="text" col="col-md-6" {...field("nom")} />
      <Field label="Prénom" placeholder="Prénom" type="text" col="col-md-6" {...field("prenom")} />
      <Field label="CIN" hint="8 chiffres" placeholder="12345678" type="text" col="col-md-6" {...field("cin")} />
      <Field label="Télephone" placeholder="+216 71 000 000" type="tel" col="col-md-6" {...field("phone")} />
      <Field
        label="Email (identifiant de connexion)"
        placeholder="prenom.nom@societe.tn"
        type="email"
        autoComplete="off"
        col="col-md-12"
        {...field("email")}
      />
      <PasswordField label="Mot de passe" hint="8 caractères minimum" {...field("password")} />
      <PasswordField label="Confirmez le mot de passe" {...field("rePassword")} />
    </div>
  );
};

const Option = ({ id, title, description, checked, onChange }) => (
  <div className="custom-control custom-checkbox mb-3">
    <input id={id} type="checkbox" className="custom-control-input" checked={checked} onChange={onChange} />
    <label className="custom-control-label" htmlFor={id}>
      <strong>{title}</strong>
      <br />
      <small className="text-muted font-weight-normal">{description}</small>
    </label>
  </div>
);

// ---- the page ----------------------------------------------------------------------------------------

const AddAdminSocieteForm = ({ onDone }) => {
  const [societe, setSociete] = useState(EMPTY_SOCIETE);
  const [admin, setAdmin] = useState(EMPTY_PERSON);
  const [manager, setManager] = useState(EMPTY_PERSON);
  // what the super admin asks to create by default for the new company
  const [defaults, setDefaults] = useState(NO_DEFAULTS);

  const [file, setFile] = useState(null);
  const [fileDataURL, setFileDataURL] = useState(null);
  const [fileError, setFileError] = useState("");

  // the errors are shown (and follow the corrections) once the form has been submitted
  const [submitted, setSubmitted] = useState(false);
  const [onConfirmAddSociete, setOnConfirmAddSociete] = useState(false);
  const [confirmSocieteModel, setConfirmSocieteModel] = useState();
  const [isCancel, setIsCancel] = useState(false);

  const setSocieteField = (name) => (e) => setSociete({ ...societe, [name]: e.target.value });
  const setAdminField = (name, value) => setAdmin({ ...admin, [name]: value });
  const setManagerField = (name, value) => setManager({ ...manager, [name]: value });
  const choose = (field) => (e) => setDefaults(applyDefaultsChoice(defaults, field, e.target.checked));

  const errors = submitted
    ? validateSocieteFormByField({
        societe,
        file,
        admin,
        manager: defaults.managerAccess ? manager : null,
      })
    : [];
  const errorOf = (id) => {
    const found = errors.find((e) => e.id === id);
    return found ? found.short : undefined;
  };
  const imageError = fileError || errorOf("societe-image");

  const exportatrice = societe.typeTVA === "Exportatrice";

  // once the company is created (confirmation closed without cancel), the form starts again empty
  useEffect(() => {
    if (!onConfirmAddSociete && confirmSocieteModel != null && !isCancel) {
      onDone();
    }
  }, [onConfirmAddSociete]); // eslint-disable-line react-hooks/exhaustive-deps

  // preview of the chosen image
  useEffect(() => {
    if (!file) {
      setFileDataURL(null);
      return undefined;
    }
    let cancelled = false;
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target.result && !cancelled) setFileDataURL(e.target.result);
    };
    reader.readAsDataURL(file);
    return () => {
      cancelled = true;
      if (reader.readyState === 1) reader.abort();
    };
  }, [file]);

  const onChangeTypeTVA = (e) => {
    const typeTVA = e.target.value;
    // an exporter does not charge VAT
    setSociete({ ...societe, typeTVA, tva: typeTVA === "Exportatrice" ? "0" : societe.tva });
  };

  const onUploadImage = (e) => {
    const selected = e.target.files && e.target.files[0];
    if (!selected) {
      return; // the picker was closed without a file
    }
    if (!/\.(jpe?g|png)$/i.test(selected.name)) {
      setFileError("L'image doit être au format JPG ou PNG");
      e.target.value = "";
    } else if (selected.size > MAX_IMAGE_SIZE) {
      setFileError("La taille de l'image ne doit pas dépasser 2 Mo");
      e.target.value = "";
    } else {
      setFileError("");
      setFile(selected);
    }
  };

  const onReset = () => {
    if (window.confirm("Vider le formulaire ?")) {
      onDone();
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    const found = validateSocieteFormByField({
      societe,
      file,
      admin,
      manager: defaults.managerAccess ? manager : null,
    });
    if (found.length > 0 || fileError) {
      const first = document.getElementById(found.length > 0 ? found[0].id : "societe-image");
      if (first) {
        first.scrollIntoView({ behavior: "smooth", block: "center" });
        first.focus({ preventScroll: true });
      }
      return;
    }

    setConfirmSocieteModel({
      title: societe.title.trim(),
      matricule: societe.matricule.trim().toUpperCase(),
      adresse: societe.adresse.trim(),
      idUserCreation: 0,
      typeSociete: societe.codeCategorie === "C" ? societe.typeSociete : "",
      codeTVA: societe.codeTVA.trim(),
      codeCategorie: societe.codeCategorie,
      numberEtabSecondaire: societe.numberEtabSecondaire.trim(),
      email: societe.email.trim(),
      numPhone: societe.phone.trim(),
      typeTVA: societe.typeTVA,
      timbre: Number(societe.timbre),
      tva: societe.tva,
      typeClient: "CLIENT",
      file,
      isServiceOnly: societe.isServiceOnly,
      admin: {
        nom: admin.nom.trim(),
        prenom: admin.prenom.trim(),
        cin: admin.cin.trim(),
        email: admin.email.trim(),
        numPhone: admin.phone.trim(),
        mdp: admin.password,
      },
      defaults: {
        centralWarehouse: defaults.centralWarehouse,
        defaultCatalog: defaults.catalog,
        manager: defaults.managerAccess
          ? {
              nom: manager.nom.trim(),
              prenom: manager.prenom.trim(),
              cin: manager.cin.trim(),
              email: manager.email.trim(),
              numPhone: manager.phone.trim(),
              mdp: manager.password,
            }
          : null,
      },
    });
    setOnConfirmAddSociete(true);
  };

  const categorieC = societe.codeCategorie === "C";

  return (
    <div className="content-wrapper noryx-green" style={{ marginTop: "0" }}>
      <GreenStyle />
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-8">
              <h1 style={{ fontSize: "1.6rem" }}>Ajouter une société et son accès administrateur</h1>
              <p className="text-muted mb-0">
                Créez la société cliente, son compte administrateur et, si vous le souhaitez, sa configuration de
                départ. Tous les champs sont obligatoires.
              </p>
            </div>
            <div className="col-sm-4">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">Home</li>
                <li className="breadcrumb-item">Sociétés</li>
                <li className="breadcrumb-item active">Ajouter</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <form onSubmit={onSubmit} noValidate>
            <div className="row">
              <div className="col-lg-8">
                <Card icon="fa-building" title="Société" subtitle="Identité, coordonnées et fiscalité de la société">
                  <Group title="Identité">
                    <Field
                      id="societe-title"
                      label="Titre"
                      placeholder="Raison sociale"
                      type="text"
                      col="col-md-5"
                      value={societe.title}
                      error={errorOf("societe-title")}
                      onChange={setSocieteField("title")}
                    />
                    <Field
                      id="societe-matricule"
                      label="Matricule fiscal"
                      hint="7 chiffres + lettre"
                      placeholder="1234567A"
                      type="text"
                      col="col-md-4"
                      value={societe.matricule}
                      error={errorOf("societe-matricule")}
                      onChange={setSocieteField("matricule")}
                    />
                    <Field
                      id="societe-etablissements"
                      label="Établissements secondaires"
                      hint="3 chiffres"
                      placeholder="000"
                      type="text"
                      col="col-md-3"
                      value={societe.numberEtabSecondaire}
                      error={errorOf("societe-etablissements")}
                      onChange={setSocieteField("numberEtabSecondaire")}
                    />
                  </Group>

                  <Group title="Coordonnées">
                    <Field
                      id="societe-email"
                      label="Email"
                      placeholder="contact@societe.tn"
                      type="email"
                      autoComplete="off"
                      value={societe.email}
                      error={errorOf("societe-email")}
                      onChange={setSocieteField("email")}
                    />
                    <Field
                      id="societe-phone"
                      label="Télephone"
                      placeholder="+216 71 000 000"
                      type="tel"
                      value={societe.phone}
                      error={errorOf("societe-phone")}
                      onChange={setSocieteField("phone")}
                    />
                    <Field
                      id="societe-adresse"
                      label="Adresse"
                      placeholder="Rue, ville"
                      type="text"
                      value={societe.adresse}
                      error={errorOf("societe-adresse")}
                      onChange={setSocieteField("adresse")}
                    />
                  </Group>

                  <Group title="Fiscalité">
                    <SelectField
                      id="societe-categorie"
                      label="Code de catégorie"
                      col="col-md-3"
                      options={CODES_CATEGORIE}
                      value={societe.codeCategorie}
                      onChange={setSocieteField("codeCategorie")}
                    />
                    {categorieC && (
                      <SelectField
                        id="societe-type"
                        label="Type de société"
                        col="col-md-3"
                        options={TYPES_SOCIETE}
                        value={societe.typeSociete}
                        onChange={setSocieteField("typeSociete")}
                      />
                    )}
                    <Field
                      id="societe-code-tva"
                      label="Code TVA"
                      placeholder="A"
                      type="text"
                      col="col-md-3"
                      value={societe.codeTVA}
                      error={errorOf("societe-code-tva")}
                      onChange={setSocieteField("codeTVA")}
                    />
                    <SelectField
                      id="societe-type-tva"
                      label="Type de TVA"
                      col="col-md-3"
                      placeholder="Choisir..."
                      options={REGIMES_TVA}
                      value={societe.typeTVA}
                      error={errorOf("societe-type-tva")}
                      onChange={onChangeTypeTVA}
                    />
                    <Field
                      id="societe-tva"
                      label="TVA ( % )"
                      col="col-md-3"
                      type="number"
                      min="0"
                      max="100"
                      step="any"
                      disabled={exportatrice}
                      help={exportatrice ? "Une société exportatrice n'applique pas de TVA" : undefined}
                      value={societe.tva}
                      error={errorOf("societe-tva")}
                      onChange={setSocieteField("tva")}
                    />
                    <Field
                      id="societe-timbre"
                      label="Timbre fiscal"
                      col="col-md-3"
                      placeholder="0"
                      type="number"
                      min="0"
                      step="any"
                      value={societe.timbre}
                      error={errorOf("societe-timbre")}
                      onChange={setSocieteField("timbre")}
                    />
                    <div className="col-md-12">
                      <div className="custom-control custom-switch mb-2">
                        <input
                          id="societe-service"
                          type="checkbox"
                          className="custom-control-input"
                          checked={societe.isServiceOnly === 1}
                          onChange={(e) => setSociete({ ...societe, isServiceOnly: e.target.checked ? 1 : 0 })}
                        />
                        <label className="custom-control-label" htmlFor="societe-service">
                          Société de service (ne gère pas de produits en stock)
                        </label>
                      </div>
                    </div>
                  </Group>
                </Card>

                <Card
                  icon="fa-user-shield"
                  title="Accès administrateur"
                  tone="success"
                  subtitle="Le compte qui administrera la société"
                >
                  <PersonFields prefix="admin" person={admin} onChange={setAdminField} errorOf={errorOf} />
                </Card>

                {defaults.managerAccess && (
                  <Card
                    icon="fa-warehouse"
                    title="Accès responsable dépôt"
                    tone="warning"
                    subtitle={
                      defaults.centralWarehouse
                        ? "Il sera responsable du dépôt central"
                        : "Aucun dépôt ne lui est affecté (pas de dépôt central demandé)"
                    }
                  >
                    <PersonFields prefix="manager" person={manager} onChange={setManagerField} errorOf={errorOf} />
                  </Card>
                )}
              </div>

              <div className="col-lg-4">
                <Card
                  icon="fa-image"
                  title="Logo de la société"
                  subtitle="Affiché sur les documents et utilisé comme photo des accès"
                  tone="secondary"
                >
                  <label
                    htmlFor="societe-image"
                    className="d-flex flex-column align-items-center justify-content-center text-center rounded mb-2"
                    style={{
                      border: `2px dashed ${imageError ? "#dc3545" : "#adb5bd"}`,
                      minHeight: 190,
                      padding: 12,
                      cursor: "pointer",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    {fileDataURL ? (
                      <img
                        src={fileDataURL}
                        alt="Aperçu du logo de la société"
                        style={{ maxHeight: 170, maxWidth: "100%" }}
                      />
                    ) : (
                      <>
                        <i className="fas fa-cloud-upload-alt fa-2x text-muted mb-2" />
                        <span>Cliquez pour choisir une image</span>
                        <small className="text-muted">JPG ou PNG, 2 Mo maximum</small>
                      </>
                    )}
                  </label>
                  <input
                    id="societe-image"
                    type="file"
                    accept=".jpg, .jpeg, .png"
                    className="sr-only"
                    onChange={onUploadImage}
                    aria-invalid={imageError ? "true" : undefined}
                  />
                  <small className="text-muted d-block">
                    {file ? `${file.name} — cliquez sur l'image pour la changer` : "Aucune image choisie"}
                  </small>
                  {imageError && <div className="invalid-feedback d-block">{imageError}</div>}
                </Card>

                <Card
                  icon="fa-cogs"
                  title="Configuration par défaut"
                  subtitle="Cochez ce qui doit être créé avec la société"
                  tone="success"
                >
                  <Option
                    id="defaults-warehouse"
                    title="Créer un dépôt central"
                    description="Un dépôt « Dépôt central » prêt à recevoir du stock. Son accès responsable est créé par défaut."
                    checked={defaults.centralWarehouse}
                    onChange={choose("centralWarehouse")}
                  />
                  <Option
                    id="defaults-manager"
                    title="Créer un accès responsable dépôt"
                    description={
                      defaults.centralWarehouse
                        ? "Coché avec le dépôt central : décochez-le pour que l'admin gère le dépôt."
                        : "Un compte « responsable dépôt » pour la société."
                    }
                    checked={defaults.managerAccess}
                    onChange={choose("managerAccess")}
                  />
                  <Option
                    id="defaults-catalog"
                    title="Créer une famille, sous-famille, catégorie et marque « DEFAUT »"
                    description="Pour pouvoir ajouter des produits dès le premier jour."
                    checked={defaults.catalog}
                    onChange={choose("catalog")}
                  />
                </Card>

                {submitted && errors.length > 0 && (
                  <div className="alert alert-danger" role="alert">
                    <i className="fas fa-exclamation-triangle mr-2" />
                    {errors.length === 1 ? "1 champ à corriger" : `${errors.length} champs à corriger`} avant
                    d'ajouter la société.
                  </div>
                )}
                <button type="submit" className="btn btn-success btn-block btn-lg">
                  <i className="fas fa-plus-circle mr-2" />
                  Ajouter la société
                </button>
                <button type="button" className="btn btn-outline-secondary btn-block mt-2 mb-4" onClick={onReset}>
                  Vider le formulaire
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>

      {onConfirmAddSociete && (
        <ConfirmAddSocieteWithAdmin
          setOnConfirmAddSociete={setOnConfirmAddSociete}
          confirmSocieteModel={confirmSocieteModel}
          setIsCancel={setIsCancel}
        />
      )}
    </div>
  );
};

// A new key gives an empty form (instead of reloading the whole page) once a company has been created.
const AddAdminSociete = () => {
  const [formKey, setFormKey] = useState(0);
  return <AddAdminSocieteForm key={formKey} onDone={() => setFormKey((key) => key + 1)} />;
};

export default AddAdminSociete;
